import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { RHFCheckboxField } from '@openx/components-core/lib/CheckboxField/RHFCheckboxField.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form.ts';
import { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getOrganizationsLabels, organizationFields, REVENUE_SHARE_FIELDS } from '../../../constants.ts';
import type { OrganizationFormData } from '../../../types.ts';
import { PartnerFields } from './components/PartnerFields.tsx';
import { RevenueShareFields } from './components/RevenueShareFields.tsx';

export type OpenAudienceAccessSectionProps = {
  isEdit: boolean;
};

export const OpenAudienceAccessSection = ({ isEdit }: OpenAudienceAccessSectionProps) => {
  const { t } = useTranslation();
  const organizationLabels = getOrganizationsLabels(t);
  const { getValues, resetField } = useFormContext<OrganizationFormData>();

  const [areCuratedDealFeesEnabled, setCuratedDealFeesEnabled] = useState(
    !!getValues(organizationFields.EXCHANGE_ACCOUNT_PARTNER_ID)
  );

  const [isRevenueShareEnabled, setIsRevenueShareEnabled] = useState(
    !!getValues(organizationFields.IS_OPENX_REVENUE_SHARE_ENABLED)
  );

  const observableShouldDisableCuratedDealFees = useWatch<
    OrganizationFormData,
    typeof organizationFields.SHOULD_DISABLE_CURATED_DEAL_FEES
  >({
    name: organizationFields.SHOULD_DISABLE_CURATED_DEAL_FEES,
  });

  const observableParentAccount = useWatch<OrganizationFormData, typeof organizationFields.PARENT_ACCOUNT>({
    name: organizationFields.PARENT_ACCOUNT,
  });

  const isTopLevelOrganization = observableParentAccount ? observableParentAccount?.depth === 0 : true;
  const shouldShowFeeSplitingWarning = !isEdit && !isTopLevelOrganization;

  const resetRevenueShareFields = useCallback(() => {
    for (const field of REVENUE_SHARE_FIELDS) {
      resetField(field, { defaultValue: '' });
    }
  }, [resetField]);

  const handleExchangeAccountPartnerIdChange = useCallback(
    (exchangePartnerId: string) => {
      if (!exchangePartnerId) {
        resetRevenueShareFields();
        resetField(organizationFields.CURATED_DEAL_BUYER_DISCOUNTS_ENABLED, { defaultValue: false });
        resetField(organizationFields.IS_OPENX_REVENUE_SHARE_ENABLED, { defaultValue: false });
        setIsRevenueShareEnabled(false);
      }
    },
    [resetField, resetRevenueShareFields]
  );

  const handleCuratedDealFeesSwitchChange = useCallback(
    (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setCuratedDealFeesEnabled(checked);

      if (!checked) {
        resetField(organizationFields.EXCHANGE_ACCOUNT_PARTNER_ID, { defaultValue: '' });
        handleExchangeAccountPartnerIdChange('');
      }
    },
    [resetField, handleExchangeAccountPartnerIdChange]
  );

  const handleRevenueShareSwitchChange = useCallback(
    (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setIsRevenueShareEnabled(checked);

      if (!checked) resetRevenueShareFields();
    },
    [resetRevenueShareFields]
  );

  // initialized with the loaded value so an edited organization keeps its existing fees -
  // fields are cleared only when the flag switches on (a organization parent is selected)
  const wereCuratedDealFeesDisabled = useRef(!!observableShouldDisableCuratedDealFees);

  useEffect(() => {
    const hasBecomeDisabled = !!observableShouldDisableCuratedDealFees && !wereCuratedDealFeesDisabled.current;
    wereCuratedDealFeesDisabled.current = !!observableShouldDisableCuratedDealFees;

    if (!hasBecomeDisabled) return;

    resetRevenueShareFields();
    resetField(organizationFields.CURATED_DEAL_BUYER_DISCOUNTS_ENABLED, { defaultValue: false });
    resetField(organizationFields.EXCHANGE_ACCOUNT_PARTNER_ID, { defaultValue: '' });
    resetField(organizationFields.IS_OPENX_REVENUE_SHARE_ENABLED, { defaultValue: false });
    setCuratedDealFeesEnabled(false);
    setIsRevenueShareEnabled(false);
  }, [observableShouldDisableCuratedDealFees, resetField, resetRevenueShareFields]);

  return (
    <FormSection
      description={t('Setup of access to 3P Data and related fields for the account.')}
      title={t('3P Data')}
      rightColumnSize={RIGHT_COLUMN_WIDTH}
    >
      <PartnerFields
        areCuratedDealFeesEnabled={areCuratedDealFeesEnabled}
        labels={organizationLabels}
        onCuratedDealFeesSwitchChange={handleCuratedDealFeesSwitchChange}
        onExchangeAccountPartnerIdChange={handleExchangeAccountPartnerIdChange}
        shouldDisableCuratedDealFees={!!observableShouldDisableCuratedDealFees}
      />

      <RevenueShareFields
        isRevenueShareEnabled={isRevenueShareEnabled}
        labels={organizationLabels}
        onRevenueShareSwitchChange={handleRevenueShareSwitchChange}
      />

      <RHFCheckboxField
        data-test="fee-splitting-partner-checkbox"
        label={organizationLabels[organizationFields.FEE_SPLITTING_PARTNER]}
        margin="dense"
        name={organizationFields.FEE_SPLITTING_PARTNER}
      />

      {shouldShowFeeSplitingWarning && (
        <Alert severity="warning" sx={{ mt: 2, alignItems: 'center', maxWidth: 'fit-content' }}>
          <Typography>
            {t('Default setting for Fee Splitting Partner is inherited from the parent organization.')}
          </Typography>
        </Alert>
      )}
    </FormSection>
  );
};
